import {
  createCampaignPopup,
  editCampaignPopupDetail,
  getCampaignPopupDetail,
} from '../../../api/campaignPopupApi'
import { uploadImage } from '../../../api/cardConfigApi'
import { FormattedMessage } from '../../../components/intl'
import config from '../../../config'
import { DATE_TIME_FORMAT } from '../../../helpers/constant'
import { getImageUrlToUploadFileInput } from '../../../helpers/image-upload-helper'
import { keysToCamel } from '../../../helpers/keys-to-case'
import { getImageUrl } from '../../../helpers/utils'
import translate from '../../../i18n'
import { request } from '../../../request'
import { UploadFileDirectoryEnum } from '../../../share-types'
import { CampaignPopupUserTypeOptions } from '../constant'
import {
  CampaignPopupFormData,
  CampaignPopupRedirectTypeEnums,
  CampaignPopupStatusEnums,
  CampaignPopupUserTypeEnums,
} from '../share-type'
import { UploadOutlined } from '@ant-design/icons'
import { Button, DatePicker, Form, Input, notification, Radio, Select, Upload } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const { RangePicker } = DatePicker

const inactiveCampaignPopupNotification = () => {
  notification['warning']({
    message: translate('campaignPopups.inactiveNotificationMessage'),
    description: translate('campaignPopups.inactiveNotificationDescription'),
    style: {
      width: 600,
    },
    placement: 'top',
  })
}

const CampaignPopupForm = () => {
  const navigate = useNavigate()
  const params = useParams()

  const [form] = Form.useForm()
  const [campaignPopupFormDisabled, setCampaignPopupFormDisabled] = useState<boolean>()
  const [loading, setLoading] = useState<boolean>(false)

  const campaignPopupId = params?.id

  useEffect(() => {
    if (campaignPopupId) {
      getCampaignPopupDetail(Number(campaignPopupId)).then((res: any) => {
        const { startDate, endDate, ...other } = keysToCamel(res)
        const campaignPopupForm = {
          rangeTime: [dayjs(startDate), dayjs(endDate)],
          ...other,
          imageUrl: getImageUrlToUploadFileInput([res.imageUrl]),
        }

        if (campaignPopupForm.status === CampaignPopupStatusEnums.INACTIVE && campaignPopupId) {
          setCampaignPopupFormDisabled(true)
          inactiveCampaignPopupNotification()
        }

        form.setFieldsValue(campaignPopupForm)
      })
    }
  }, [campaignPopupId])

  const onFinish = (formData: CampaignPopupFormData) => {
    const { rangeTime, ...other } = formData

    const bodyFormData = {
      startDate: rangeTime[0],
      endDate: rangeTime[1],
      ...other,
    }
    bodyFormData.imageUrl = getImageUrl(
      'imageUrl',
      `${config.assetUrl}/${UploadFileDirectoryEnum.CAMPAIGN_POPUP_IMAGES}`,
      formData.imageUrl,
    )
    setLoading(true)

    let promise
    if (campaignPopupId) {
      promise = editCampaignPopupDetail(Number(campaignPopupId), bodyFormData).then(res => {
        notification.success({
          message: translate('campaignPopups.updateSuccess'),
        })
        navigate(`/campaign-popups/${res.id}`)
      })
    } else {
      promise = createCampaignPopup(bodyFormData).then(res => {
        notification.success({
          message: translate('campaignPopups.createSuccess'),
        })
        navigate(`/campaign-popups/${res.id}`)
      })
    }
    promise
      .catch(error => {
        notification.error({
          message: translate('apiError'),
          description: error.message ?? '',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e
    }
    return e.fileList
  }

  const uploadUrl = (options: any, directory: UploadFileDirectoryEnum) => {
    const { onSuccess, onError, file, filename } = options
    const fileExtension = file.name.split('.').pop()
    const imageFilename = `${filename}_${file.uid}.${fileExtension}`
    uploadImage(imageFilename, directory)
      .then(res => {
        onSuccess(res)
        request({
          url: res,
          method: 'PUT',
          data: file,
          headers: {
            'x-amz-acl': 'public-read',
          },
        })
      })
      .catch(err => {
        onError({ err })
      })
  }

  return (
    <Form
      name="campaign_popup"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 12 }}
      initialValues={{
        summary: '',
        status: CampaignPopupStatusEnums.INTERNAL_ONLY,
        userType: CampaignPopupUserTypeEnums.ALL_USER,
      }}
      autoComplete="off"
      form={form}
      onFinish={onFinish}
    >
      <Form.Item
        label={translate('campaignPopups.summary')}
        name="summary"
        rules={[{ required: true, message: translate('campaignPopups.summaryRuleMessage') }]}
      >
        <TextArea showCount maxLength={100} />
      </Form.Item>

      <Form.Item
        label={translate('campaignPopups.imageUrl')}
        name="imageUrl"
        valuePropName="fileList"
        getValueFromEvent={normFile}
        rules={[{ required: true, message: translate('campaignPopups.imageUrlRuleMessage') }]}
      >
        <Upload
          maxCount={1}
          name="imageUrl"
          customRequest={config => uploadUrl(config, UploadFileDirectoryEnum.CAMPAIGN_POPUP_IMAGES)}
          listType="picture"
        >
          <Button icon={<UploadOutlined />}>
            <FormattedMessage id="clickToUpload" />
          </Button>
        </Upload>
      </Form.Item>

      <Form.Item
        label={translate('campaignPopups.rangeTime')}
        name="rangeTime"
        rules={[{ required: true, message: translate('campaignPopups.rangeTimeRuleMessage') }]}
      >
        <RangePicker showTime format={DATE_TIME_FORMAT} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        label={translate('campaignPopups.redirectUrl')}
        name="redirectUrl"
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (getFieldValue('redirectType') && !value) {
                return Promise.reject(
                  new Error(translate('campaignPopups.redirectTypeRuleMessage')),
                )
              }
              return Promise.resolve()
            },
          }),
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label={translate('campaignPopups.redirectType')}
        name="redirectType"
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (getFieldValue('redirectUrl') && !value) {
                return Promise.reject(
                  new Error(translate('campaignPopups.redirectTypeRuleMessage')),
                )
              }
              return Promise.resolve()
            },
          }),
        ]}
      >
        <Select allowClear>
          <Select.Option value={CampaignPopupRedirectTypeEnums.DEEP_LINK}>
            {CampaignPopupRedirectTypeEnums.DEEP_LINK}
          </Select.Option>
          <Select.Option value={CampaignPopupRedirectTypeEnums.EXTERNAL}>
            {CampaignPopupRedirectTypeEnums.EXTERNAL}
          </Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        label={translate('campaignPopups.userType')}
        name="userType"
        rules={[{ required: true, message: translate('campaignPopups.userTypeRuleMessage') }]}
      >
        <Select options={CampaignPopupUserTypeOptions} />
      </Form.Item>

      <Form.Item
        name="status"
        label={translate('campaignPopups.status')}
        rules={[{ required: true, message: translate('campaignPopups.statusRuleMessage') }]}
      >
        <Radio.Group>
          <Radio value={CampaignPopupStatusEnums.ACTIVE}>
            {translate('campaignPopups.statusRadioActive')}
          </Radio>
          <Radio value={CampaignPopupStatusEnums.INTERNAL_ONLY}>
            {translate('campaignPopups.statusRadioInternalOnly')}
          </Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
        <Button
          disabled={campaignPopupFormDisabled}
          loading={loading}
          type="primary"
          htmlType="submit"
        >
          {translate('submit')}
        </Button>
      </Form.Item>
    </Form>
  )
}

export default () => <CampaignPopupForm />
